import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { setupMFA, verifyMFASetup, logout } from '../../redux/actions/userActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import Loader from '../../components/Loader';
import MFASetupStyles from './MFASetup.module.css';

const FRONTEND_MFA_SETUP_ERROR_CODES = {
	INVALID_SESSION: ErrorCodes.AUTH_SESSION_EXPIRED,
	SETUP_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	VERIFY_FAILED: ErrorCodes.API_INVALID_PARAMETER,
};

const buildMFASetupSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const MFASetupScreen = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	
	const [tempSession, setTempSession] = useState('');
	const [qrCode, setQrCode] = useState('');
	const [secret, setSecret] = useState('');
	const [code, setCode] = useState('');
	const [loading, setLoading] = useState(true);
	const [verifying, setVerifying] = useState(false);
	const [error, setError] = useState('');
	const [setupUnavailable, setSetupUnavailable] = useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = useState('');
	const [copiedSupportError, setCopiedSupportError] = useState(false);

	const setMFASetupSupportError = useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildMFASetupSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearMFASetupSupportError = useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (copyError) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	useEffect(() => {
		// Get temp session from location state or URL params
		const tempSessionToken = location.state?.temp_session || new URLSearchParams(location.search).get('temp_session');
		
		if (!tempSessionToken) {
			const sourceMessage = 'Invalid session. Please log in again.';
			setError(sourceMessage);
			setSetupUnavailable(true);
			setMFASetupSupportError(
				FRONTEND_MFA_SETUP_ERROR_CODES.INVALID_SESSION,
				'MFA Setup Page / Missing Temporary Session',
				sourceMessage
			);
			toast.error(buildMFASetupSupportError(
				FRONTEND_MFA_SETUP_ERROR_CODES.INVALID_SESSION,
				'MFA Setup Page / Missing Temporary Session',
				sourceMessage
			));
			setLoading(false);
			return;
		}

		setTempSession(tempSessionToken);

		// Fetch MFA setup data
		const fetchSetupData = async () => {
			try {
				setLoading(true);
				const data = await dispatch(setupMFA(tempSessionToken));
				
				if (data.status) {
					setQrCode(data.qr_code);
					setSecret(data.secret);
					setSetupUnavailable(false);
					clearMFASetupSupportError(true);
				} else {
					const sourceMessage = data.msg || 'Failed to setup MFA';
					setError(sourceMessage);
					setSetupUnavailable(true);
					setMFASetupSupportError(
						FRONTEND_MFA_SETUP_ERROR_CODES.SETUP_LOAD_FAILED,
						'MFA Setup Page / Setup Data Load Failed',
						sourceMessage
					);
					toast.error(buildMFASetupSupportError(
						FRONTEND_MFA_SETUP_ERROR_CODES.SETUP_LOAD_FAILED,
						'MFA Setup Page / Setup Data Load Failed',
						sourceMessage
					));
				}
			} catch (err) {
				const sourceMessage = err?.response?.data?.msg || err?.message || 'Error setting up MFA';
				setError(sourceMessage);
				setSetupUnavailable(true);
				setMFASetupSupportError(
					FRONTEND_MFA_SETUP_ERROR_CODES.SETUP_LOAD_FAILED,
					'MFA Setup Page / Setup Data Request Error',
					sourceMessage
				);
				toast.error(buildMFASetupSupportError(
					FRONTEND_MFA_SETUP_ERROR_CODES.SETUP_LOAD_FAILED,
					'MFA Setup Page / Setup Data Request Error',
					sourceMessage
				));
			} finally {
				setLoading(false);
			}
		};

		fetchSetupData();
	}, [clearMFASetupSupportError, dispatch, location, setMFASetupSupportError]);

	const handleCodeChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
		setCode(value);
		setError('');
	};

	const handleVerify = async () => {
		if (code.length !== 6) {
			const sourceMessage = 'Please enter a 6-digit code';
			setError(sourceMessage);
			setMFASetupSupportError(
				FRONTEND_MFA_SETUP_ERROR_CODES.VERIFY_FAILED,
				'MFA Setup Page / Verification Validation Failed',
				sourceMessage
			);
			return;
		}

		try {
			setVerifying(true);
			setError('');
			const data = await dispatch(verifyMFASetup(code, tempSession));
			
			if (data.status) {
				toast.success('MFA setup successful! Redirecting...');
				clearMFASetupSupportError(true);
				setTimeout(() => {
					navigate('/');
				}, 1000);
			} else {
				const sourceMessage = data.msg || 'Invalid verification code';
				setError(sourceMessage);
				setMFASetupSupportError(
					FRONTEND_MFA_SETUP_ERROR_CODES.VERIFY_FAILED,
					'MFA Setup Page / Verification Failed',
					sourceMessage
				);
			}
		} catch (err) {
			const sourceMessage = err?.response?.data?.msg || err?.message || 'Error verifying code. Please try again.';
			setError(sourceMessage);
			setMFASetupSupportError(
				FRONTEND_MFA_SETUP_ERROR_CODES.VERIFY_FAILED,
				'MFA Setup Page / Verification Request Error',
				sourceMessage
			);
		} finally {
			setVerifying(false);
		}
	};

	const handleLogout = () => {
		dispatch(logout());
		navigate('/login');
	};

	const handleKeyPress = (e) => {
		if (e.key === 'Enter' && code.length === 6) {
			handleVerify();
		}
	};

	if (loading) {
		return (
			<div className={MFASetupStyles.container}>
				<Loader />
			</div>
		);
	}

	if (setupUnavailable) {
		return (
			<div className={MFASetupStyles.container} style={{ backgroundImage: 'url(/assets/login-bg.jpg)' }}>
				<div className={MFASetupStyles.setupCard}>
					<div className={MFASetupStyles.header}>
						<img src='/assets/logo-new.png' alt='Logo' className={MFASetupStyles.logo} />
						<h1 className={MFASetupStyles.title}>MFA Setup Unavailable</h1>
						<p className={MFASetupStyles.subtitle}>
							Unable to continue MFA setup for this session.
						</p>
					</div>
					{error && <p className={MFASetupStyles.error}>{error}</p>}
					{supportErrorMessage && (
						<div className={MFASetupStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={MFASetupStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={MFASetupStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={MFASetupStyles.copySupportErrorButton}
									onClick={() => clearMFASetupSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={MFASetupStyles.actions}>
						<Button
							label='Back To Login'
							onClick={() => navigate('/login')}
							style={{ width: '100%' }}
						/>
						<Button
							label='Logout'
							onClick={handleLogout}
							style={{
								width: '100%',
								backgroundColor: 'transparent',
								border: '1px solid var(--border-color)',
								color: 'var(--text-secondary)',
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={MFASetupStyles.container} style={{ backgroundImage: 'url(/assets/login-bg.jpg)' }}>
			<div className={MFASetupStyles.setupCard}>
				<div className={MFASetupStyles.header}>
					<img src='/assets/logo-new.png' alt='Logo' className={MFASetupStyles.logo} />
					<h1 className={MFASetupStyles.title}>Setup Two-Factor Authentication</h1>
					<p className={MFASetupStyles.subtitle}>
						MFA is required to enhance the security of your account
					</p>
				</div>

				<div className={MFASetupStyles.content}>
					{supportErrorMessage && (
						<div className={MFASetupStyles.supportErrorBanner}>
							<div className={MFASetupStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={MFASetupStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={MFASetupStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={MFASetupStyles.copySupportErrorButton}
									onClick={() => clearMFASetupSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={MFASetupStyles.step}>
						<div className={MFASetupStyles.stepNumber}>1</div>
						<div className={MFASetupStyles.stepContent}>
							<h3 className={MFASetupStyles.stepTitle}>Install Authenticator App</h3>
							<p className={MFASetupStyles.stepDescription}>
								Download Google Authenticator or Microsoft Authenticator from your app store
							</p>
						</div>
					</div>

					<div className={MFASetupStyles.step}>
						<div className={MFASetupStyles.stepNumber}>2</div>
						<div className={MFASetupStyles.stepContent}>
							<h3 className={MFASetupStyles.stepTitle}>Scan QR Code</h3>
							<p className={MFASetupStyles.stepDescription}>
								Open your authenticator app and scan this QR code:
							</p>
							{qrCode && (
								<div className={MFASetupStyles.qrCodeContainer}>
									<img src={qrCode} alt='MFA QR Code' className={MFASetupStyles.qrCode} />
								</div>
							)}
						</div>
					</div>

					<div className={MFASetupStyles.step}>
						<div className={MFASetupStyles.stepNumber}>3</div>
						<div className={MFASetupStyles.stepContent}>
							<h3 className={MFASetupStyles.stepTitle}>Or Enter Secret Manually</h3>
							<p className={MFASetupStyles.stepDescription}>
								If you can't scan the QR code, enter this secret in your app:
							</p>
							<div className={MFASetupStyles.secretBox}>{secret}</div>
						</div>
					</div>

					<div className={MFASetupStyles.step}>
						<div className={MFASetupStyles.stepNumber}>4</div>
						<div className={MFASetupStyles.stepContent}>
							<h3 className={MFASetupStyles.stepTitle}>Enter Verification Code</h3>
							<p className={MFASetupStyles.stepDescription}>
								Enter the 6-digit code from your authenticator app:
							</p>
							<TextInput
								label='Verification Code'
								placeholder='000000'
								value={code}
								onChange={handleCodeChange}
								onKeyPress={handleKeyPress}
								maxLength={6}
								autoFocus
								style={{ fontSize: '20px', letterSpacing: '8px', textAlign: 'center' }}
							/>
							{error && <p className={MFASetupStyles.error}>{error}</p>}
						</div>
					</div>

					<div className={MFASetupStyles.actions}>
						<Button
							label={verifying ? 'Verifying...' : 'Verify & Complete Setup'}
							onClick={handleVerify}
							disabled={code.length !== 6 || verifying}
							style={{ width: '100%' }}
						/>
						<Button
							label='Logout'
							onClick={handleLogout}
							style={{
								width: '100%',
								backgroundColor: 'transparent',
								border: '1px solid var(--border-color)',
								color: 'var(--text-secondary)',
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default MFASetupScreen;

